"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@focal/ui-kit";
import { Button } from "@focal/ui-kit";
import { Input } from "@focal/ui-kit";
import { z } from "zod";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@focal/ui-kit";
import {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CLIENT_ALL_GET,
  CLIENT_GET,
  CONVERSATION_GET,
  PARTICIPANTS_GET,
  createClient,
  createParticipant,
  getConversation,
  updateClient,
  updateParticipant,
} from "@/api/client";
import {
  Client,
  ClientRole,
  CrmType,
  CLIENT_ROLE_LABELS,
  Participant,
} from "@/types";

import { toast } from "sonner";
import { showErrorToast } from "@/lib/errorUtils";
import { formatPhoneNumber, getPrimaryEmail, removeNonNumeric } from "@/lib";
import { mapCRMName } from "../../ClientsListPage/utils/mapCRMName";
import { Tooltip, TooltipContent, TooltipTrigger, Icon } from "@focal/ui-kit";
import { ImageBasedOnCrm } from "@/components/pages/Conversations/ConversationsDetails/TaskManagerModal/components/ImageBasedOnCrm";
import { useCrm } from "@/components/pages/Settings/integrations/hooks";
import { LoadingSpinner, Separator } from "@focal/ui-kit";
import {
  ClientEmailList,
  ClientPhoneList,
  ContactListHandle,
  EmailDraft,
  EmailDraftList,
  PhoneDraft,
  PhoneDraftList,
  areEmailDraftsValid,
  arePhoneDraftsValid,
  newEmailDraft,
  newPhoneDraft,
} from "@/components/pages/Clients/components/ContactLists";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";
import { CloudEventType, CloudEvent } from "@/constants/websocket-events";

// Shared scalar fields. Email/phone are NOT part of the create form: they are
// staged as multi-row drafts (EmailDraftList / PhoneDraftList) and sent as the
// `emailAddresses` / `phoneNumbers` arrays in the single create request, since
// the backend create endpoint accepts only those arrays (and rejects top-level
// `email` / `phone` under forbidNonWhitelisted).
const formSchema = z.object({
  firstname: z
    .string()
    .min(2, "First name is too short")
    .max(50, "First name is too long"),
  lastname: z
    .string()
    .min(2, "Last name is too short")
    .max(50, "Last name is too long"),
  role: z.nativeEnum(ClientRole),
});

// In edit mode the primary email/phone fields aren't shown — the contact lists
// own them — so the form still carries the client's mirror values (kept via
// shouldUnregister: false) but they must not block saving (a client can
// legitimately have no email if all addresses were removed). Keep format
// validation when present.
const editFormSchema = formSchema.extend({
  email: z.string().email("Invalid email").or(z.literal("")),
  phone: z
    .string()
    .min(10, "Phone number must have at least 10 digits")
    .optional()
    .or(z.literal("")),
});

// Superset type for the shared react-hook-form instance. Edit mode populates
// email/phone; create mode leaves them unset (they live in the draft lists).
type ClientFormValues = z.infer<typeof editFormSchema>;

interface ClientModalProps {
  client?: Client;
  conversationId?: string;
  participantId?: string;
  speakerId?: string | null;
  defaultValues?: Partial<Client>;
  onSuccess?: (data?: Participant) => void;
  handleDelete?: () => void;
}

export const ClientModal = ({
  client,
  conversationId,
  participantId,
  defaultValues,
  children,
  onSuccess,
  speakerId,
  handleDelete,
}: PropsWithChildren<ClientModalProps>) => {
  const { crmType, isLoading, isContactSyncEnabled, isConnected } = useCrm();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // The contact lists stage value edits locally and only persist them when this
  // modal is saved. Hold a ref to each so onSubmit can flush those edits before
  // updating the client. Immediate actions (add / delete / make-primary) are
  // handled inside the lists and are not part of the flush.
  const emailListRef = useRef<ContactListHandle>(null);
  const phoneListRef = useRef<ContactListHandle>(null);

  // Create-flow only: emails/phones are staged locally as multi-row drafts and
  // sent as arrays in the create request. (Edit mode uses the API-backed
  // ClientEmailList / ClientPhoneList instead.) Both are optional: a list holds
  // only what the prefill seeds (below) plus whatever the user adds via
  // "Add email" / "Add phone", and a client can be created with just a name.
  //
  // Callers prefill the create form via `defaultValues` — the conversation
  // participant popover, for instance, passes the email Focal already extracted
  // for the person being added. The create form has no top-level email/phone
  // inputs (the draft lists own those values), so the prefill has to be seeded
  // into the drafts; otherwise it is dropped and the user retypes an address
  // Focal already knows.
  const seedEmailDrafts = useCallback((): EmailDraft[] => {
    const email = defaultValues?.email?.trim();
    return email ? [{ ...newEmailDraft(true), email }] : [];
  }, [defaultValues?.email]);

  const seedPhoneDrafts = useCallback((): PhoneDraft[] => {
    // Normalize exactly as PhoneDraftList does while typing, so a seeded row can
    // never sit in a state the list itself wouldn't produce.
    const digits = removeNonNumeric(defaultValues?.phone ?? "").slice(0, 10);
    return digits
      ? [{ ...newPhoneDraft(true), value: formatPhoneNumber(digits) }]
      : [];
  }, [defaultValues?.phone]);

  const [emailDrafts, setEmailDrafts] = useState<EmailDraft[]>(seedEmailDrafts);
  const [phoneDrafts, setPhoneDrafts] = useState<PhoneDraft[]>(seedPhoneDrafts);

  // Drops whatever the user staged and returns the lists to the prefill.
  const resetDrafts = useCallback(() => {
    setEmailDrafts(seedEmailDrafts());
    setPhoneDrafts(seedPhoneDrafts());
  }, [seedEmailDrafts, seedPhoneDrafts]);

  const handleSuccess = (data?: Participant) => {
    onSuccess && onSuccess(data);
  };

  // WebSocket CloudEvent handler for conversation updates
  const handleWebSocketMessage = useCallback(
    (event: CloudEvent) => {
      const eventConversationId = (event.data as { conversationId?: string })
        ?.conversationId;

      // Only handle events for this conversation
      if (eventConversationId !== conversationId) {
        return;
      }

      // Handle conversation status and processing events
      switch (event.type) {
        case CloudEventType.CONVERSATION_STATUS_UPDATED:
        case CloudEventType.CONVERSATION_PROCESSING_COMPLETED:
          // CloudEvents contain minimal data - invalidate to fetch full conversation
          queryClient.invalidateQueries({
            queryKey: [CONVERSATION_GET, conversationId],
          });
          break;
      }
    },
    [conversationId, queryClient],
  );

  // Use global WebSocket subscription for this conversation
  const { isConnected: isWebSocketConnected } = useWebSocketSubscription(
    "conversation",
    conversationId,
    handleWebSocketMessage,
    !!conversationId,
  );

  useQuery({
    queryKey: [CONVERSATION_GET, conversationId],
    queryFn: () => getConversation(conversationId),
    enabled: !!conversationId,
    refetchInterval: () => {
      // Don't poll if WebSocket is connected - rely on real-time updates
      if (isWebSocketConnected) return false;

      // Poll every 10 seconds as fallback when WebSocket is not connected
      return 10000;
    },
  });

  const { mutate: onCreateParticipant } = useMutation({
    mutationFn: (data: Partial<Participant>) => createParticipant(data),
    onSuccess: (data) => {
      setOpen(false);
      queryClient.setQueryData(
        [PARTICIPANTS_GET, conversationId],
        (oldData: Participant[] | undefined) => {
          if (!data) return oldData;
          return [...(oldData ?? []), data];
        },
      );
      if (data) {
        handleSuccess(data);
      }
    },
    onError: (error) => {
      showErrorToast(error, "client");
    },
  });

  const { mutate: onUpdateParticipant } = useMutation({
    mutationFn: (data: Partial<Participant>) => updateParticipant(data),
    onSuccess: (data, participant) => {
      setOpen(false);
      queryClient.setQueryData(
        [PARTICIPANTS_GET, conversationId],
        (oldData: Participant[] | undefined) => {
          if (!data) return oldData;
          return (oldData ?? []).map((p) =>
            p.id === participant.id ? data : p,
          );
        },
      );
      handleSuccess(data);
    },
    onError: (error) => {
      showErrorToast(error, "client");
    },
  });

  const { mutate: createOrUpdateClient, isPending: isSaving } = useMutation({
    mutationFn: client ? updateClient : createClient,
    onSuccess: (data) => {
      toast.success(`Client ${client ? "updated" : "created"} successfully`);
      setOpen(false);
      if (client) {
        // Refresh the client data if updating
        queryClient.invalidateQueries({
          queryKey: [CLIENT_GET, client.id],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_ALL_GET],
        });
        // Programmatic close (above) doesn't fire onOpenChange, so clear the
        // staged drafts and form fields here — otherwise the next "Create client"
        // reopens with the previous client's name/role still populated.
        resetDrafts();
        form.reset();
      }

      const name = `${data?.firstname || ""} ${data?.lastname || ""}`;
      if (participantId && conversationId) {
        onUpdateParticipant({
          id: participantId,
          clientId: data?.id,
          conversationId: conversationId,
          name,
        });
      } else if (data && conversationId) {
        onCreateParticipant({
          email: data.email,
          name,
          conversationId: conversationId,
          clientId: data.id,
          speakerId: speakerId,
        });
      } else if (data) {
        // No conversation yet (pre-meeting flows): hand the created client
        // back as a synthetic participant so the caller can add it locally.
        handleSuccess({
          id: data.id,
          clientId: data.id,
          name,
          email: getPrimaryEmail(data),
          client: data,
        });
      } else {
        handleSuccess();
      }
    },
    onError: (error) => {
      showErrorToast(error, "client");
    },
  });

  const form = useForm<ClientFormValues>({
    // The create schema omits email/phone (they live in the draft lists), so the
    // two schemas have different shapes and the conditional resolver widens to a
    // generic type. Cast to the form's superset value type — create simply
    // leaves email/phone at their (unused) defaults.
    resolver: zodResolver(
      client ? editFormSchema : formSchema,
    ) as unknown as Resolver<ClientFormValues>,
    defaultValues: {
      firstname: client?.firstname || defaultValues?.firstname || "",
      lastname: client?.lastname || defaultValues?.lastname || "",
      email: client?.email || defaultValues?.email || "",
      phone: client?.phone || defaultValues?.phone || "",
      role: client?.role || ClientRole.Client,
    },
    // Keep values for fields that aren't mounted. In edit mode the primary
    // email/phone inputs aren't rendered (the lists own them), so unregistering
    // would drop their values to `undefined` and fail the resolver's email
    // check — silently blocking Save. Retaining the client's values keeps the
    // form valid. (No effect on the create flow, where every field is mounted.)
    shouldUnregister: false,
  });

  // Manual-submit modal — no isDirty guard, or sticky-dirty would block agent writes.
  useEffect(() => {
    if (!client) return;
    const next = {
      firstname: client.firstname || "",
      lastname: client.lastname || "",
      email: client.email || "",
      phone: client.phone || "",
      role: client.role || ClientRole.Client,
    };
    const current = form.getValues();
    if (
      next.firstname !== current.firstname ||
      next.lastname !== current.lastname ||
      next.email !== current.email ||
      next.phone !== current.phone ||
      next.role !== current.role
    ) {
      form.reset(next);
    }
  }, [client, form]);

  // Create-flow contact validity: email and phone are both optional, so the
  // only requirement is that any rows the user *did* enter are well-formed.
  // Empty lists are fine — a client can be created with just a name.
  const isCreateContactWellFormed =
    areEmailDraftsValid(emailDrafts) && arePhoneDraftsValid(phoneDrafts);

  async function onSubmit(values: ClientFormValues) {
    if (!client) {
      // Create flow: send the staged email/phone drafts as arrays. The backend
      // create endpoint persists all child rows in one request (it does not
      // accept top-level email/phone). Guarded by the disabled Create button,
      // but re-check here so an invalid draft can never reach the request.
      if (!isCreateContactWellFormed) {
        return;
      }

      const emailAddresses = emailDrafts
        .map((draft) => ({ email: draft.email.trim(), primary: draft.primary }))
        .filter((draft) => draft.email.length > 0);

      const phoneNumbers = phoneDrafts
        .map((draft) => ({
          number: removeNonNumeric(draft.value),
          type: draft.type,
          primary: draft.primary,
        }))
        .filter((draft) => draft.number.length === 10);

      createOrUpdateClient({
        firstname: values.firstname,
        lastname: values.lastname,
        role: values.role,
        emailAddresses,
        ...(phoneNumbers.length > 0 ? { phoneNumbers } : {}),
      });
      return;
    }

    // Edit flow: persist any staged email/phone value edits first.
    // flushPendingEdits never rejects; it returns how many edits failed (each
    // failure already surfaced its own error toast). If any failed, bail before
    // the client update so we don't fire a contradictory "updated successfully"
    // toast and close the modal — leave it open so the user can fix the value
    // (e.g. an email that collides with another contact's primary) and retry.
    const flushFailures = await Promise.all([
      emailListRef.current?.flushPendingEdits() ?? Promise.resolve(0),
      phoneListRef.current?.flushPendingEdits() ?? Promise.resolve(0),
    ]);
    if (flushFailures.some((count) => count > 0)) {
      return;
    }

    const changedValues = Object.keys(values).reduce((acc, key) => {
      const valueKey = key as keyof ClientFormValues;
      const value = values[valueKey];
      if (value && value !== client?.[key as keyof Client]) {
        (acc as Record<string, unknown>)[valueKey] = value;
      }
      return acc;
    }, {} as Partial<Client>);

    createOrUpdateClient({
      ...changedValues,
      id: client.id,
    });
  }

  // Laylah has no contact write-back, so client details are read-only in
  // Focal whenever the user is connected to it — edits must happen in Laylah.
  const isLaylahConnected = crmType === CrmType.LAYLAH;

  const isCreateButtonDisabled = useMemo(() => {
    if (client) return false;
    return isContactSyncEnabled;
  }, [client, isContactSyncEnabled]);

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        if (state) {
          // useForm captures defaultValues at mount, but callers may derive them
          // from a live search box. Re-apply on open so the prefill reflects
          // what was typed rather than the first render's (empty) value.
          if (!client) {
            form.reset({
              firstname: defaultValues?.firstname || "",
              lastname: defaultValues?.lastname || "",
              email: defaultValues?.email || "",
              phone: defaultValues?.phone || "",
              role: ClientRole.Client,
            });
            // Same reason as the form reset: re-seed rather than relying on the
            // drafts' mount-time state, so the lists show the latest prefill.
            resetDrafts();
          }
        } else {
          form.reset();
          resetDrafts();
        }
        setOpen(state);
        if (!state) handleSuccess();
      }}
    >
      <DialogTrigger asChild>
        {children ? (
          children
        ) : isCreateButtonDisabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="default"
                  disabled
                  iconLeft={
                    !client ? <Icon name="AddIcon" /> : <Icon name="EditIcon" />
                  }
                >
                  {client ? "Edit profile" : "New client"}
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {/* Firm has contact sync on, but this user hasn't connected their
                  own CRM yet — pointing them at "their CRM" is confusing, so
                  direct them to connect it instead. Once connected, fall back to
                  the standard "manage clients in your CRM" guidance. */}
              {isConnected
                ? "If contact sync is on, you can't create clients in Focal. Please create clients directly in your CRM instead."
                : "Connect your CRM to import clients."}
            </TooltipContent>
          </Tooltip>
        ) : client &&
          client.crmContact?.crmId != null &&
          !isContactSyncEnabled ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={true}
                  iconLeft={<Icon name="EditIcon" />}
                >
                  Edit profile
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Please enable CRM contact sync to edit imported clients.
            </TooltipContent>
          </Tooltip>
        ) : client && isLaylahConnected ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={true}
                  iconLeft={<Icon name="EditIcon" />}
                  data-cy="edit-client"
                >
                  Edit profile
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Client details are managed in Laylah. Please edit this client in
              Laylah instead.
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant={client ? "outline" : "default"}
            size={client ? "sm" : "default"}
            disabled={isLoading}
            iconLeft={
              !client ? <Icon name="AddIcon" /> : <Icon name="EditIcon" />
            }
            data-cy="add-client"
          >
            {client ? "Edit profile" : "New client"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] flex max-h-[90vh] flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {client ? (
              <div className="flex items-center">
                Edit client
                {/* Only show the CRM badge for clients actually synced from a
                    CRM (crmContact.crmId set). `crmType` alone just means the
                    firm has a CRM connected, which wrongly tagged Focal-created,
                    unsynced clients and promised changes would sync back. */}
                {crmType && client.crmContact?.crmId != null && (
                  <div className="ml-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <ImageBasedOnCrm
                            crmType={crmType}
                            size={
                              crmType === CrmType.PARAGON_MICROSOFTDYNAMICS
                                ? 16
                                : 24
                            }
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p className="whitespace-pre-wrap max-w-[220px] text-center">
                          {`Changes here will be reflected in ${mapCRMName(crmType)}`}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            ) : (
              "Create client"
            )}
          </DialogTitle>
          <DialogDescription>
            {client
              ? "Update the client details below."
              : "Enter the client details below."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            noValidate
            // onSubmit only reads the contact-list refs at submit time (inside
            // the deferred handler), never during render — safe despite the rule.
            // eslint-disable-next-line react-hooks/refs
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex min-h-0 flex-col gap-8"
          >
            {/* The dialog is a flex column capped at 90vh; this body grows with
                its content but shrinks and scrolls (min-h-0 + overflow) when
                the dialog hits that cap, so on short screens the pinned header
                and footer stay on-screen. A plain overflow container is used
                rather than <ScrollArea>, whose `h-full` stretched it to fill
                the row and left a dead gap below short content. */}
            <div className="grid gap-4 py-4 min-h-0 overflow-y-auto -mx-3 px-3">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name*</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name*</FormLabel>
                    <FormControl>
                      <Input type="text" placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Create flow only: emails/phones are staged as multi-row drafts
                    and sent as arrays in the create request. When editing, the
                    API-backed email/phone lists below own these values (and the
                    primary), so the drafts are not shown there. */}
              {!client && (
                <div className="flex flex-col gap-4">
                  <EmailDraftList
                    value={emailDrafts}
                    onChange={setEmailDrafts}
                  />
                  <PhoneDraftList
                    value={phoneDrafts}
                    onChange={setPhoneDrafts}
                  />
                </div>
              )}
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(ClientRole).map((role) => (
                          <SelectItem key={role} value={role}>
                            {CLIENT_ROLE_LABELS[role]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {client && (
                <>
                  <Separator />
                  <div className="flex flex-col gap-4">
                    <ClientEmailList ref={emailListRef} client={client} />
                    <ClientPhoneList ref={phoneListRef} client={client} />
                  </div>
                </>
              )}
            </div>
            <DialogFooter className="flex justify-between! flex-row w-full shrink-0">
              {client ? (
                <>
                  {/* Block deletion in Focal only for clients that actually
                      exist in the CRM (crmContact.crmId) while sync is on —
                      those must be deleted in the CRM. Focal-created, unsynced
                      clients aren't in the CRM, so they'd otherwise be
                      undeletable anywhere; allow deleting them here. */}
                  {client.crmContact?.crmId != null && isContactSyncEnabled ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <Button
                            variant="destructiveOutline"
                            type="button"
                            disabled
                            onClick={() => {
                              if (handleDelete) {
                                handleDelete();
                                setOpen(false);
                              }
                            }}
                          >
                            Delete client
                          </Button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        If contact sync is on, you can&apos;t delete clients in
                        Focal. Please delete clients directly in your CRM
                        instead.
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="destructiveOutline"
                      type="button"
                      onClick={() => {
                        if (handleDelete) {
                          handleDelete();
                          setOpen(false);
                        }
                      }}
                    >
                      Delete client
                    </Button>
                  )}
                </>
              ) : (
                <div />
              )}

              <Button
                type="submit"
                disabled={
                  isSaving || (!client && !isCreateContactWellFormed)
                }
              >
                {isSaving && <LoadingSpinner className="h-4 w-4 mr-2" />}
                {client ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
